import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { InvoiceSummaryDto } from 'src/generated/services/TFinancialApi';
import { ShipmentSelect } from '../../molecules/shipment/ShipmentSelect';
import { TableSelectedShipments } from './TableSelectedShipments';

interface FormReprocessShipmentProps {
  invoiceSummary: InvoiceSummaryDto;
  isLoading: boolean;
  onShipmentSelect?: (shipmentId: string) => void;
}

export const FormReprocessShipment = ({ invoiceSummary, isLoading, onShipmentSelect }: FormReprocessShipmentProps) => {
  // Get form methods from react-hook-form context
  const { setValue, watch } = useFormContext();
  const selectedShipmentIds = watch('shipmentInvoiceId');

  // Get all shipments from all categories to pass to the table
  const allShipments = useMemo(() => {
    if (!invoiceSummary?.shipmentList) return [];

    const shipments = [
      ...(invoiceSummary.shipmentList.ACTION_NEEDED || []),
      ...(invoiceSummary.shipmentList.PENDING || []),
      ...(invoiceSummary.shipmentList.RESOLVED || []),
      ...(invoiceSummary.shipmentList.PROCESSING || []),
      ...(invoiceSummary.shipmentList.UNDER_REVIEW || []),
    ];

    return shipments;
  }, [invoiceSummary?.shipmentList]);

  const formattedSelection = useMemo(() => {
    if (Array.isArray(selectedShipmentIds)) {
      return selectedShipmentIds;
    }
    if (selectedShipmentIds) {
      return [selectedShipmentIds];
    }
    return [];
  }, [selectedShipmentIds]);

  // Handle shipment selection
  const handleSelectShipment = (shipmentId: string | string[]) => {
    setValue('shipmentInvoiceId', shipmentId);

    // Notify parent component about the selection
    if (onShipmentSelect) {
      // If we're dealing with an array but the parent expects a string,
      // pass the first selected item or an empty string
      const valueToPass = Array.isArray(shipmentId) ? shipmentId[0] || '' : shipmentId;
      onShipmentSelect(valueToPass);
    }
  };

  return (
    <>
      <div className='mt-4'>
        {invoiceSummary && !isLoading && (
          <ShipmentSelect
            shipmentList={invoiceSummary.shipmentList}
            shipmentInvoiceId={formattedSelection}
            onSelectShipment={handleSelectShipment}
            isProcurement={invoiceSummary?.currentMainRole === 'PROCUREMENT'}
            isOperation={invoiceSummary?.currentMainRole === 'OPERATION'}
            multiple
          />
        )}
        {isLoading && <div className='p-4'>Loading shipment data...</div>}
        {!isLoading && formattedSelection.length > 0 && (
          <div className='mt-16'>
            <TableSelectedShipments
              shipments={formattedSelection}
              shipmentData={allShipments}
              onDelete={(shipmentId) => {
                const newSelection = formattedSelection.filter((id) => id !== shipmentId);
                handleSelectShipment(newSelection);
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};
